import { useEffect, useState } from "react";
import { Package, Truck, Clock, Users, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';

function EcommerceDeliveryPage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sliderImages = [
    "/assest/inside-content/Delivery-inside-slider/1.jpg",
    "/assest/inside-content/Delivery-inside-slider/2.jpg",
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + sliderImages.length) % sliderImages.length);
  };

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, []);

  const benefits = [
    {
      icon: <DollarSign className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Competitive Salary",
      description: "Salary range from ₹50,000 to ₹100,000 based on experience and role"
    },
    {
      icon: <Users className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Skilled Workforce",
      description: "Access to trained professionals in logistics and customer service"
    },
    {
      icon: <Clock className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Flexible Scheduling",
      description: "Adaptable staffing solutions for peak seasons and special events"
    },
    {
      icon: <Truck className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Last-Mile Delivery",
      description: "Specialized staff for efficient last-mile delivery operations"
    }
  ];

  const roles = [
    {
      title: "Delivery Associates",
      salary: "₹50,000 - ₹70,000",
      responsibilities: ["Package handling", "Route optimization", "Customer interaction", "Timely deliveries"]
    },
    {
      title: "Warehouse Staff",
      salary: "₹55,000 - ₹75,000",
      responsibilities: ["Inventory management", "Order picking & packing", "Quality control", "Safety compliance"]
    },
    {
      title: "Logistics Coordinators",
      salary: "₹70,000 - ₹90,000",
      responsibilities: ["Route planning", "Team coordination", "Performance tracking", "Customer support"]
    },
    {
      title: "Operations Managers",
      salary: "₹80,000 - ₹100,000",
      responsibilities: ["Team leadership", "Process optimization", "Strategic planning", "Quality assurance"]
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Banner Slider */}
      <section className="relative h-[400px] sm:h-[500px] md:h-[600px] w-full overflow-hidden">
        {/* Slider Images */}
        {sliderImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt={`E-Commerce & Delivery Services ${index + 1}`}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: index === 0 ? 'center 60%' : 'center 35%' }}
            />
          </div>
        ))}
        
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent z-10" />
        
        <div className="container relative z-20 h-full px-5 sm:px-6 flex items-center">
          <div className="max-w-3xl">
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6">
              E-Commerce & <br />Delivery Services
            </h1>
            <p className="font-body text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed max-w-2xl">
              Powering your logistics with skilled professionals and innovative workforce solutions
            </p>
          </div>
        </div>

        {/* Navigation Buttons */}
        <button
          onClick={prevSlide}
          className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 z-30 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-110"
          aria-label="Previous slide"
        >
          <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
        
        <button
          onClick={nextSlide}
          className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 z-30 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 sm:p-3 rounded-full transition-all duration-300 hover:scale-110"
          aria-label="Next slide"
        >
          <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>

        {/* Dots Indicator */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-30 flex gap-2 sm:gap-3">
          {sliderImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`h-2 sm:h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-gold w-6 sm:w-8' 
                  : 'bg-white/50 hover:bg-white/80 w-2 sm:w-3'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Introduction Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="max-w-4xl mx-auto text-center">
            <span className="section-label mb-3 sm:mb-4 inline-block text-xs sm:text-sm">
              Our Expertise
            </span>
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] lg:text-[52px] font-bold text-brand-navy leading-tight mb-6">
              Comprehensive Staffing Solutions for E-Commerce
            </h2>
            <p className="text-text-dark text-base sm:text-lg leading-relaxed mb-8">
              In today's fast-paced digital marketplace, having the right team is crucial for success. 
              We provide specialized manpower solutions for e-commerce and delivery operations, ensuring 
              your business runs smoothly with trained, dedicated professionals who understand the demands 
              of modern logistics and customer service.
            </p>
            <blockquote className="border-l-4 border-gold pl-6 my-8">
              <p className="font-display italic text-lg sm:text-xl text-text-medium leading-relaxed">
                "The best way to predict the future is to create it through exceptional service and dedicated teams."
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="bg-gray-50 py-12 sm:py-16 md:py-20">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] font-bold text-brand-navy leading-tight">
              Why Choose Our Services
            </h2>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white p-6 sm:p-8 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-gold mb-4">
                  {benefit.icon}
                </div>
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-brand-navy mb-3">
                  {benefit.title}
                </h3>
                <p className="text-text-dark text-sm sm:text-base leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Roles & Salary Section */}
      <section className="bg-white py-12 sm:py-16 md:py-20 lg:py-16">
        <div className="container mx-auto px-5 sm:px-6 lg:px-4">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <span className="section-label mb-3 sm:mb-4 inline-block text-xs sm:text-sm">
              Career Opportunities
            </span>
            <h2 className="font-display text-3xl sm:text-[36px] md:text-[42px] lg:text-[52px] font-bold text-brand-navy leading-tight mb-4">
              Positions & Compensation
            </h2>
            <p className="text-text-dark text-base sm:text-lg max-w-3xl mx-auto">
              We offer competitive salary packages ranging from ₹50,000 to ₹100,000 monthly, 
              depending on role, experience, and expertise.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto">
            {roles.map((role, index) => (
              <div 
                key={index}
                className="bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-brand-navy">
                    {role.title}
                  </h3>
                  <Package className="w-6 h-6 text-gold flex-shrink-0 ml-2" />
                </div>
                
                <div className="inline-block bg-gold/10 text-gold px-4 py-2 rounded-lg mb-4">
                  <span className="font-semibold text-sm sm:text-base">{role.salary}</span>
                  <span className="text-xs ml-1">per month</span>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold text-brand-navy text-sm uppercase tracking-wide mb-3">
                    Key Responsibilities:
                  </h4>
                  <ul className="space-y-2">
                    {role.responsibilities.map((resp, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-gold mr-2 mt-1">•</span>
                        <span className="text-text-dark text-sm sm:text-base">{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default EcommerceDeliveryPage;

